import { useRef, useState } from 'react'
import { FileUp, Loader2, Paperclip, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { apiClient } from '@/lib/api/client'
import type { HelmOutboundCommunicationAttachment } from '@/lib/api/types'

const MAX_ATTACHMENT_COUNT = 25
const MAX_RAW_ATTACHMENT_BYTES = 3 * 1024 * 1024

interface CommunicationAttachmentPickerProps {
  value: HelmOutboundCommunicationAttachment[]
  onChange: (attachments: HelmOutboundCommunicationAttachment[]) => void
  onError: (message: string | null) => void
  onUploadingChange?: (uploading: boolean) => void
  disabled?: boolean
}

function formatBytes(value: number | null | undefined) {
  if (!value) return ''
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KiB`
  return `${(value / 1024 / 1024).toFixed(1)} MiB`
}

export function CommunicationAttachmentPicker({
  value,
  onChange,
  onError,
  onUploadingChange,
  disabled,
}: CommunicationAttachmentPickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  async function uploadSelectedFiles(files: File[]) {
    if (!files.length) return
    if (value.length + files.length > MAX_ATTACHMENT_COUNT) {
      onError(`A message can include at most ${MAX_ATTACHMENT_COUNT} attachments.`)
      return
    }

    const currentBytes = value.reduce(
      (total, attachment) => total + (attachment.size_bytes || 0),
      0,
    )
    const selectedBytes = files.reduce((total, file) => total + file.size, 0)
    if (
      files.some((file) => file.size > MAX_RAW_ATTACHMENT_BYTES) ||
      currentBytes + selectedBytes > MAX_RAW_ATTACHMENT_BYTES
    ) {
      onError(
        'Attachments must total 3 MiB or less so the complete email remains within the provider limit.',
      )
      return
    }

    setUploading(true)
    onUploadingChange?.(true)
    onError(null)
    try {
      const uploaded: HelmOutboundCommunicationAttachment[] = []
      for (const file of files) {
        uploaded.push(await apiClient.uploadCommunicationAttachment(file))
      }
      onChange([...value, ...uploaded])
    } catch (error) {
      onError(
        error instanceof Error
          ? `${error.message} Choose the file again to retry.`
          : 'The attachment could not be uploaded. Choose the file again to retry.',
      )
    } finally {
      setUploading(false)
      onUploadingChange?.(false)
    }
  }

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="sr-only"
        disabled={disabled || uploading}
        onChange={(event) => {
          const files = Array.from(event.target.files || [])
          event.target.value = ''
          void uploadSelectedFiles(files)
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || uploading}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading ? <Loader2 className="size-4 animate-spin" /> : <FileUp className="size-4" />}
        {uploading ? 'Uploading' : 'Attach files'}
      </Button>
      {value.length ? (
        <div className="flex flex-wrap gap-2" aria-label="Uploaded attachments">
          {value.map((attachment, index) => (
            <div
              key={attachment.storage_key || `${attachment.file_name}-${index}`}
              className="inline-flex min-w-0 items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5 text-sm"
            >
              <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="max-w-56 truncate">{attachment.file_name || 'Attachment'}</span>
              {attachment.size_bytes ? (
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatBytes(attachment.size_bytes)}
                </span>
              ) : null}
              <button
                type="button"
                className="rounded-sm text-muted-foreground hover:text-foreground"
                aria-label={`Remove ${attachment.file_name || 'attachment'}`}
                disabled={disabled || uploading}
                onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
              >
                <X className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Files upload before sending. Combined attachment size is limited to 3 MiB.
      </p>
    </div>
  )
}
